import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ShieldCheck, Lock, Pulse, Signature, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isPortalHost, isPreviewHost, homeFor } from "@/lib/host";
import { AuthShell, TopoPattern, BrandGlow } from "@/components/auth/AuthShell";
import { SignInForm } from "@/components/auth/SignInForm";
import { TokuroLogo } from "@/components/brand/TokuroLogo";

const CLIENT_PORTAL_BG =
  "https://static.prod-images.emergentagent.com/jobs/50295861-6594-4cf0-9f18-ec913189d33f/images/17b03c7dc980eb367985fa91d13bc811e7c258bbe7a4bbf0f48ba207bef68df9.jpeg";

function BrandLogo() {
  return (
    <TokuroLogo
      variant="light"
      size="lg"
      showTagline
      testId="brand-logo"
    />
  );
}

function BulletIcon({ Icon, label }) {
  return (
    <li className="flex items-start gap-3 text-[15px] text-white/85">
      <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center border border-[#B79148]/40 bg-[#B79148]/10">
        <Icon size={17} weight="regular" className="text-[#B79148]" />
      </span>
      <span className="pt-1.5">{label}</span>
    </li>
  );
}

/* ─── STAFF DOOR ─────────────────────────────────────────────────────────── */

function StaffLeftPanel() {
  return (
    <>
      <TopoPattern />
      <BrandGlow />
      <div className="relative z-10">
        <BrandLogo />
      </div>
      <div className="relative z-10 max-w-lg">
        <h1 className="font-editorial text-[44px] font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[56px]">
          Security work,
          <br />
          <span className="italic text-[#C9A15B]">handled with discipline.</span>
        </h1>
        <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/70">
          The internal workspace for proposals, GRC delivery and secure knowledge.
        </p>
        <ul className="mt-10 space-y-4">
          <BulletIcon Icon={FileText} label="Proposals and engagement delivery" />
          <BulletIcon Icon={ShieldCheck} label="GRC evidence, controls and assurance" />
          <BulletIcon Icon={Lock} label="Secure Internal knowledge base" />
        </ul>
      </div>
      <p className="relative z-10 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
        Confidential · Authorized staff only
      </p>
    </>
  );
}

function StaffDoor() {
  const [prefill, setPrefill] = useState(null);
  return (
    <AuthShell testId="staff-login-page" leftPanel={<StaffLeftPanel />}>
      <p className="text-center font-mono text-[11px] font-medium uppercase tracking-[0.28em] text-[#B79148]">
        Secure Access · Staff
      </p>
      <h2 className="mt-3 text-center font-heading text-[32px] font-extrabold leading-tight tracking-tight text-[#0A0F2E] sm:text-4xl">
        Sign in to your workspace
      </h2>
      <p className="mt-2 text-center text-sm text-[#0A0F2E]/60">
        Use your DAAKYI credentials to continue.
      </p>

      <div className="mt-8">
        <SignInForm
          prefill={prefill}
          emailPlaceholder="you@daakyi.com"
          showSSO
        />
      </div>

      {isPreviewHost() && (
        <div className="mt-6 rounded-lg border border-dashed border-border bg-white/60 p-4" data-testid="demo-accounts-panel">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            Demo accounts · password Daakyi@2026
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[
              ["Super Admin", "admin@daakyi.com"],
              ["Platform Admin", "platformadmin@daakyi.com"],
              ["Workspace Owner", "owner@daakyi.com"],
              ["Contributor", "contributor@daakyi.com"],
              ["Reviewer", "reviewer@daakyi.com"],
              ["Viewer", "viewer@daakyi.com"],
              ["Client (Northwind)", "ceo@northwind-demo.com"],
            ].map(([label, em]) => (
              <button
                key={em}
                type="button"
                onClick={() => setPrefill({ email: em, password: "Daakyi@2026" })}
                data-testid={`demo-${em}`}
                className="rounded border border-border bg-white px-2 py-1 font-mono text-[10px] text-foreground transition hover:border-[#B79148] hover:text-[#B79148]"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="mt-8 text-center text-xs text-[#0A0F2E]/50">
        Protected by multi-factor authentication · All access is logged.
      </p>
    </AuthShell>
  );
}

/* ─── CLIENT DOOR ────────────────────────────────────────────────────────── */

function ClientLeftPanel() {
  return (
    <>
      <img
        src={CLIENT_PORTAL_BG}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-40"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F2E]/85 via-[#0A0F2E]/70 to-[#0A0F2E]/95" aria-hidden="true" />
      <TopoPattern className="opacity-70" />
      <BrandGlow />

      <div className="relative z-10">
        <BrandLogo />
      </div>
      <div className="relative z-10 max-w-lg">
        <h1 className="font-editorial text-[44px] font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[56px]">
          Your security posture,
          <br />
          <span className="italic text-[#C9A15B]">visible at all times.</span>
        </h1>
        <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/70">
          One confidential place for your engagement, deliverables and assurance.
        </p>
        <ul className="mt-10 space-y-4">
          <BulletIcon Icon={Pulse} label="Live security posture, continuously assured" />
          <BulletIcon Icon={ShieldCheck} label="Approved deliverables — reviewed before you see them" />
          <BulletIcon Icon={Signature} label="Sign-offs and requests in one confidential place" />
        </ul>
      </div>
      <p className="relative z-10 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
        Confidential · For authorized client representatives only
      </p>
    </>
  );
}

function ClientDoor() {
  return (
    <AuthShell testId="client-login-page" leftPanel={<ClientLeftPanel />}>
      <p className="text-center font-mono text-[11px] font-medium uppercase tracking-[0.28em] text-[#B79148]">
        Client Portal · By Invitation
      </p>
      <h2 className="mt-3 text-center font-heading text-[32px] font-extrabold leading-tight tracking-tight text-[#0A0F2E] sm:text-4xl">
        Welcome back
      </h2>
      <p className="mt-2 text-center text-sm text-[#0A0F2E]/60">
        Sign in to view your engagement and live security posture.
      </p>

      <div className="mt-8">
        <SignInForm
          emailPlaceholder="you@yourcompany.com"
          submitLabel="Sign in to your portal"
          showSSO={false}
        />
      </div>

      <div
        className="mt-6 flex items-start gap-3 rounded-lg border-l-4 border-[#B79148] bg-[#B79148]/5 p-3.5"
        data-testid="portal-invite-note"
      >
        <EnvelopeSimple className="mt-0.5 h-4 w-4 shrink-0 text-[#B79148]" weight="bold" />
        <p className="text-xs leading-relaxed text-[#0A0F2E]/75">
          Access is by invitation only. Accounts are provisioned by your DAAKYI engagement team —
          if you need access, contact your engagement lead.
        </p>
      </div>

      <p className="mt-8 text-center text-xs text-[#0A0F2E]/50">
        Protected by multi-factor authentication · All access is logged.
      </p>
    </AuthShell>
  );
}

export default function Login({ portal = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(homeFor(user), { replace: true });
  }, [user, navigate]);

  return portal || isPortalHost() ? <ClientDoor /> : <StaffDoor />;
}
